'use client';

import React, { useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import i18n, { i18nPt, i18nEn } from '@/i18n';
import { useLanguageStore } from '@/store/useLanguageStore';
import type { ForcedSiteLanguage } from '@/lib/country';

interface I18nProviderProps {
  children: React.ReactNode;
  /**
   * Idioma impuesto por el país del tenant (lo resuelve el layout del servidor
   * con `forcedSiteLanguage`): 'pt' para Brasil, 'en' para EE.UU. Sin valor, el
   * idioma se decide exactamente como siempre (store persistido / default del
   * tenant).
   */
  forcedLanguage?: ForcedSiteLanguage | null;
}

/** La instancia fija de cada idioma obligatorio, y su `<html lang>`. */
const FORZADAS = {
  pt: { instancia: i18nPt, lang: 'pt-BR' },
  en: { instancia: i18nEn, lang: 'en-US' },
} as const;

export function I18nProvider({ children, forcedLanguage }: I18nProviderProps) {
  const { currentLanguage, setInitialized } = useLanguageStore();
  const forzada =
    forcedLanguage === 'pt' || forcedLanguage === 'en' ? FORZADAS[forcedLanguage] : null;
  const forced = forzada !== null;
  const langForzado = forzada?.lang;

  useEffect(() => {
    // Sitio de Brasil / EE.UU.: el idioma no lo decide el store. La instancia
    // fija ya está en su idioma; solo se asegura el atributo lang (el layout ya
    // lo emite).
    if (forced) {
      if (typeof document !== 'undefined' && langForzado) {
        document.documentElement.lang = langForzado;
      }
      setInitialized(true);
      return;
    }

    // Inicializar i18n con el idioma del store
    if (currentLanguage) {
      i18n.changeLanguage(currentLanguage);
      if (typeof document !== 'undefined') {
        document.documentElement.lang = currentLanguage;
      }
    }

    // Marcar como inicializado
    setInitialized(true);
  }, [forced, langForzado, currentLanguage, setInitialized]);

  return <I18nextProvider i18n={forzada ? forzada.instancia : i18n}>{children}</I18nextProvider>;
}
